import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@es-tools/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBrandConfig } from '../brand-config';

export interface BrandHeaderAction {
  label: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
}

interface BrandHeaderProps {
  actionLinks?: BrandHeaderAction[];
  actionsSlot?: React.ReactNode;
  showPrimaryNav?: boolean;
}

const DEFAULT_PORTAL_LINK: BrandHeaderAction = {
  label: '← Back to Portal',
  href: import.meta.env.VITE_PORTAL_URL || 'https://automations.easystreetbrands.com',
  external: true,
};

type PrimaryNavItem = {
  label: string;
  href: string;
  isActive: (pathname: string) => boolean;
  openInNewTab?: boolean;
  sameOriginApp?: boolean;
};

const BrandHeader: React.FC<BrandHeaderProps> = ({
  actionLinks,
  actionsSlot,
  showPrimaryNav = true,
}) => {
  const { user, logout } = useAuth();
  const brandConfig = getBrandConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const linksToRender = actionLinks && actionLinks.length > 0 ? actionLinks : [DEFAULT_PORTAL_LINK];
  const aiCenterApp = brandConfig.apps.find((app) => app.id === 'ai-center' && app.enabled && !app.external);
  const dataPlatformApp = brandConfig.apps.find(
    (app) => app.id === 'data-platform' && app.enabled && !app.external,
  );
  const primaryNavItems: PrimaryNavItem[] = [
    {
      label: 'Home',
      href: '/',
      isActive: (pathname) => pathname === '/',
    },
    ...(aiCenterApp
      ? [
          {
            label: 'AI Center',
            href: aiCenterApp.path,
            isActive: (pathname: string) => pathname.startsWith('/ai-center'),
            sameOriginApp: aiCenterApp.sameOriginApp,
          },
        ]
      : []),
    ...(dataPlatformApp
      ? [
          {
            label: 'Data Platform',
            href: dataPlatformApp.path,
            isActive: (pathname: string) => pathname.startsWith('/data-platform'),
            openInNewTab: true,
          },
        ]
      : []),
    {
      label: 'Automation Library',
      href: '/automation-library',
      isActive: (pathname) => pathname.startsWith('/automation-library'),
    },
    {
      label: 'Holy Shipped',
      href: '/holy-shipped',
      isActive: (pathname) => pathname.startsWith('/holy-shipped'),
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="header">
      <div className="header-content">
          <div className="brand-info">
            <div className="brand-logo-container">
              <img
                src={brandConfig.logo}
                alt={`${brandConfig.name} Logo`}
              className="brand-logo"
            />
            </div>
          </div>
        {showPrimaryNav && (
          <nav className="header-primary-nav" aria-label={`${brandConfig.name} primary navigation`}>
            {primaryNavItems.map((item) => {
              const className = `header-primary-link ${item.isActive(location.pathname) ? 'active' : ''}`;
              if (item.openInNewTab) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={className}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.label}
                  </a>
                );
              }
              if (item.sameOriginApp) {
                return (
                  <a key={item.href} href={item.href} className={className}>
                    {item.label}
                  </a>
                );
              }
              return (
                <button
                  key={item.href}
                  type="button"
                  className={className}
                  onClick={() => navigate(item.href)}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        )}
        <div className="header-actions">
          {linksToRender.map((action, index) => {
            if (action.onClick && !action.href) {
              return (
                <button
                  key={index}
                  type="button"
                  className="portal-link"
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              );
            }

            const { href = '#', external } = action;
            const isInternalNavigation = !external && href.startsWith('/');
            return (
              <a
                key={index}
                href={href}
                className="portal-link"
                onClick={
                  isInternalNavigation
                    ? (event) => {
                        event.preventDefault();
                        navigate(href);
                      }
                    : undefined
                }
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
              >
                {action.label}
              </a>
            );
          })}

          {actionsSlot}

          <div className="user-dropdown" ref={dropdownRef}>
            <button
              className="user-dropdown-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="user-avatar" />
              ) : (
                <div className="user-avatar-placeholder">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12">
                <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <span className="dropdown-user-name">{user?.name}</span>
                  <span className="dropdown-user-email">{user?.email}</span>
                </div>
                <div className="dropdown-divider" />
                <button onClick={logout} className="dropdown-item dropdown-signout">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6M11 11L14 8M14 8L11 5M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default BrandHeader;
